import { FREE_STRETCH_COUNT, STRETCH_COUNT } from '../lib/product.js'
import { MONTHLY_PRICE, YEARLY_PRICE } from '../lib/facts.js'

// Every claim here must be traceable to the URL in its `sources` entry. When a
// vendor changes something, edit it here and every comparison page follows.
// A null means "not verified", and is never rendered as a yes or a no.

export const LAST_VERIFIED = '7 October 2026'

export const SOURCES = {
    keybreakPricing: 'https://keybreak.co.uk/pricing/',
    keybreakPrivacy: 'https://keybreak.co.uk/privacy/',
    keybreakApp: 'https://keybreak.co.uk/break-reminder-app/',
    keybreakEula: 'https://keybreak.co.uk/eula/',
    keybreakLanguages: 'https://keybreak.co.uk/blog/multiple-languages/',
    stretchlyAbout: 'https://hovancik.net/stretchly/about',
    stretchlyReadme: 'https://github.com/hovancik/stretchly#readme',
    stretchlyIdeas: 'https://github.com/hovancik/stretchly/blob/trunk/app/locales/en.json',
    stretchlyLocales: 'https://github.com/hovancik/stretchly/tree/trunk/app/locales',
    workrave: 'https://workrave.org/',
    workraveDownload: 'https://workrave.org/download/',
    workraveExercises: 'https://github.com/rcaelers/workrave/blob/main/ui/data/exercises/exercises.xml.in',
    timeOut: 'https://www.dejal.com/timeout/',
    restier: 'https://restier.app/en/',
    restierPricing: 'https://restier.app/en/pricing/',
    restierMeetings: 'https://restier.app/en/docs/smart-pauses-and-schedule/',
    breakTimer: 'https://breaktimer.app/',
    breakTimerReadme: 'https://github.com/tom-james-watson/breaktimer-app#readme',
}

export const COMPETITORS = {
    keybreak: {
        name: 'Keybreak',
        url: 'https://keybreak.co.uk/',
        platforms: 'macOS (Apple Silicon and Intel), Windows',
        price: `Keybreak Plus: ${MONTHLY_PRICE}/month (one month free) or ${YEARLY_PRICE}/year. No lifetime licence.`,
        freeTier: `Free for personal use: break reminders and ${FREE_STRETCH_COUNT} of the ${STRETCH_COUNT} stretches, no account.`,
        openSource: false,
        guidedStretches: `${STRETCH_COUNT} stretches, each filmed with a real person, with the hold timed on screen.`,
        guidedStretchCount: STRETCH_COUNT,
        humanDemonstration: true,
        holdTimer: true,
        injuryAware: 'Yes (Keybreak Plus): movements you flag are never suggested.',
        focusAreas: 'Yes (Keybreak Plus)',
        activityAware: true,
        meetingAware: false,
        multiMonitor: null,
        languages: null,
        bestFor: 'Being shown exactly which stretch to do, leaving out movements you have chosen to avoid.',
        watchOut: 'Mac and Windows only, and breaks are timed from your activity and chosen hours rather than from your calendar.',
        sourceUrl: SOURCES.keybreakPricing,
        sources: {
            platforms: SOURCES.keybreakPricing,
            price: SOURCES.keybreakPricing,
            freeTier: SOURCES.keybreakPricing,
            guidedStretches: SOURCES.keybreakPricing,
            injuryAware: SOURCES.keybreakPricing,
        },
        lastVerified: LAST_VERIFIED,
    },
    stretchly: {
        name: 'Stretchly',
        url: 'https://hovancik.net/stretchly/',
        platforms: 'macOS (13 or later), Windows, Linux',
        price: 'Free. Optional donations.',
        freeTier: 'Everything. Free and open source (BSD-2-Clause).',
        openSource: true,
        guidedStretches: '87 one-line text break ideas, many naming a movement and a hold time. No video or illustrations by default.',
        guidedStretchCount: 0,
        humanDemonstration: false,
        holdTimer: null,
        injuryAware: 'No',
        focusAreas: false,
        activityAware: true,
        meetingAware: 'Pauses breaks while Do Not Disturb is on',
        multiMonitor: true,
        languages: 54,
        bestFor: 'A free, open-source break timer on any desktop OS, including Linux.',
        watchOut: 'Each break idea is a single line of text, and nothing screens a suggestion against an injury.',
        sourceUrl: SOURCES.stretchlyAbout,
        sources: {
            platforms: SOURCES.stretchlyReadme,
            price: SOURCES.stretchlyAbout,
            freeTier: SOURCES.stretchlyReadme,
            guidedStretches: SOURCES.stretchlyIdeas,
            activityAware: SOURCES.stretchlyAbout,
            meetingAware: SOURCES.stretchlyAbout,
            multiMonitor: SOURCES.stretchlyAbout,
            languages: SOURCES.stretchlyLocales,
        },
        lastVerified: LAST_VERIFIED,
    },
    workrave: {
        name: 'Workrave',
        url: 'https://workrave.org/',
        platforms: 'Windows, Linux. macOS only as an untested development build.',
        price: 'Free.',
        freeTier: 'Everything. Free software (GPL).',
        openSource: true,
        guidedStretches: '16 exercises, shown with drawn illustrations.',
        guidedStretchCount: 16,
        humanDemonstration: false,
        holdTimer: null,
        injuryAware: 'No',
        focusAreas: false,
        activityAware: true,
        meetingAware: null,
        multiMonitor: null,
        languages: null,
        bestFor: 'Strict, RSI-focused break enforcement on Windows and Linux, with micro-breaks, rest breaks and a daily limit.',
        watchOut: 'No stable macOS build: the Mac port is a development version the project says has not been thoroughly tested.',
        sourceUrl: SOURCES.workraveDownload,
        sources: {
            platforms: SOURCES.workraveDownload,
            price: SOURCES.workraveDownload,
            freeTier: SOURCES.workraveDownload,
            guidedStretches: SOURCES.workraveExercises,
            activityAware: SOURCES.workrave,
        },
        lastVerified: LAST_VERIFIED,
    },
    timeOut: {
        name: 'Time Out',
        url: 'https://www.dejal.com/timeout/',
        platforms: 'macOS only. Also on the Mac App Store and Setapp.',
        price: 'Core features always free. Optional non-renewing Supporter purchases from $4.99.',
        freeTier: 'The core break features, always free.',
        openSource: false,
        guidedStretches: 'None. The screen dims and a break theme appears (text, images, a web page or a video).',
        guidedStretchCount: 0,
        humanDemonstration: false,
        holdTimer: false,
        injuryAware: 'No',
        focusAreas: false,
        activityAware: true,
        meetingAware: null,
        multiMonitor: null,
        languages: null,
        bestFor: 'The easiest Mac-native break timer to try first.',
        watchOut: 'Mac only, and the break shows a theme rather than demonstrating a stretch.',
        sourceUrl: SOURCES.timeOut,
        sources: {
            platforms: SOURCES.timeOut,
            price: SOURCES.timeOut,
            freeTier: SOURCES.timeOut,
            guidedStretches: SOURCES.timeOut,
            activityAware: SOURCES.timeOut,
        },
        lastVerified: LAST_VERIFIED,
    },
    restier: {
        name: 'Restier',
        url: 'https://restier.app/en/',
        platforms: 'macOS 11+, Windows 10+, Linux (x86_64)',
        price: '€7.99/year or €19.99 lifetime for one device; 2- and 5-device tiers. 14-day money-back guarantee.',
        freeTier: 'None. A 14-day trial with every feature, no card required.',
        openSource: false,
        guidedStretches: 'Visual resets, breathing prompts and posture cues. No filmed demonstrations.',
        guidedStretchCount: 0,
        humanDemonstration: false,
        holdTimer: null,
        injuryAware: 'No',
        focusAreas: false,
        activityAware: true,
        meetingAware: 'Yes: avoids interrupting calls',
        multiMonitor: true,
        languages: null,
        bestFor: 'The cheapest paid option, with a lifetime licence, meeting detection and a Linux build.',
        watchOut: 'No free tier: after the 14-day trial you have to buy a licence to keep using it.',
        sourceUrl: SOURCES.restierPricing,
        sources: {
            platforms: SOURCES.restierPricing,
            price: SOURCES.restierPricing,
            freeTier: SOURCES.restierPricing,
            guidedStretches: SOURCES.restier,
            activityAware: SOURCES.restier,
            meetingAware: SOURCES.restier,
            multiMonitor: SOURCES.restier,
        },
        lastVerified: LAST_VERIFIED,
    },
    breakTimer: {
        name: 'BreakTimer',
        url: 'https://breaktimer.app/',
        platforms: 'Windows, macOS, Linux',
        price: 'Free.',
        freeTier: 'Everything. Free and open source (GPL-3.0).',
        openSource: true,
        guidedStretches: 'None built in. A notification or a fullscreen break window, with a message you can customise.',
        guidedStretchCount: 0,
        humanDemonstration: false,
        holdTimer: null,
        injuryAware: 'No',
        focusAreas: false,
        activityAware: true,
        meetingAware: null,
        multiMonitor: null,
        languages: null,
        bestFor: 'A simple, free, configurable break timer that stays out of the way.',
        watchOut: 'There is no exercise guidance: the break is whatever message you set.',
        sourceUrl: SOURCES.breakTimer,
        sources: {
            platforms: SOURCES.breakTimer,
            price: SOURCES.breakTimerReadme,
            freeTier: SOURCES.breakTimerReadme,
            guidedStretches: SOURCES.breakTimerReadme,
            activityAware: SOURCES.breakTimerReadme,
        },
        lastVerified: LAST_VERIFIED,
    },
}

export const TABLE_COLUMNS = [
    { key: 'bestFor', label: 'Best for' },
    { key: 'platforms', label: 'Platforms' },
    { key: 'price', label: 'Price' },
    { key: 'freeTier', label: 'Free tier (what you actually get)' },
    { key: 'guidedStretches', label: 'Guided stretches' },
    { key: 'injuryAware', label: 'Injury-aware' },
    { key: 'watchOut', label: 'Main watch-out' },
]

export const ALL_APPS = ['keybreak', 'stretchly', 'workrave', 'timeOut', 'restier', 'breakTimer']
